import { useSortable } from '@dnd-kit/sortable';
import type { Row, RowData, TableFeatures } from '@tanstack/react-table';
import type { ScrollToOptions, VirtualItem } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { type KeyboardEvent, type PointerEvent, type JSX, useRef } from 'react';

import { StylingProps } from '../../../core/types/styling-props.js';
import { isTargetInteractive } from '../../utils/is-target-interactive.js';
import {
  DataTableRowContextProvider,
  useDataTable,
} from '../contexts/DataTableContext.js';
import * as tableStyles from '../DataTable.sty.js';
import { DataTablePinningShadowCells } from './ColumnPinning/DataTablePinningShadowCells.js';
import { DataTableCell } from './DataTableCell.js';
import * as styles from './DataTableRow.sty.js';
import { rowDetailsExpandedRowCell } from '../features/RowDetails/RowDetails.sty.js';
import { useRowInteractivityProps } from '../features/RowInteractivity/hooks/useRowInteractivityProps.js';
import { SubRowCellContextProvider } from '../features/SubRows/components/SubRowCellContext.js';

/** Duration (ms) for long press on touch devices. */
const ROW_LONG_PRESS_TIMEOUT_DURATION = 500;

interface DataTableRowProps extends StylingProps {
  /**
   * DataTable row object.
   */
  row: Row<TableFeatures, RowData>;
  /**
   * The virtual item in the row virtualizer.
   */
  virtualRow: VirtualItem;
  /**
   * The index of the row in the virtual items array, representing the currently rendered rows.
   */
  virtualItemIndex: number;
  /**
   * Total number of rows currently rendered in the table.
   */
  rowCount: number;
  /**
   * The virtual items in the column virtualizer.
   */
  virtualColumns: VirtualItem[];
  /**
   * Whether the row is currently being reordered via drag and drop.
   * The dragged row is the original row that is rendered in the table.
   */
  isDraggedRow: boolean;
  /**
   * Whether the row is currently in the drag overlay.
   * The row that is in the overlay is a copy of the row being dragged.
   */
  isInDragOverlay: boolean;
  /**
   * Parity of the row to keep track of the zebra stripe patterns.
   */
  rowParity?: 'even' | 'odd';
  /**
   * Measure function for row sizing that is called when the row is rendered to the dom.
   */
  rowMeasure?: (node: Element | null) => void;
  /**
   * Measure function for column sizing that is called when the column cells are rendered to the dom.
   */
  columnMeasure?: (node: Element | null) => void;
  /**
   * rowVirtualizer function for scrolling to the row with the specified index.
   */
  scrollToIndex?: (index: number, options?: ScrollToOptions) => void;
}

/**
 * Wrapper around all cells in a props.row.
 * @internal
 */
export function DataTableRow(props: Readonly<DataTableRowProps>): JSX.Element {
  const {
    virtualRow,
    rowCount,
    virtualColumns,
    isDraggedRow,
    isInDragOverlay,
    virtualItemIndex,
    rowParity,
    rowMeasure,
    columnMeasure,
    scrollToIndex: scrollToIndexProp,
  } = props;

  const table = useDataTable();
  const virtualRowIndex = virtualRow.index;
  const hasSubRows = table.options.enableExpanding === true;
  const subRowIndicatorColumnId = hasSubRows
    ? table.getSubRowIndicatorColumnId()
    : null;
  const subRowDepth = hasSubRows ? props.row.getSubRowDepth() : 0;
  const thresholdPill = props.row.getThresholdPill();

  const floatingMenuState = table.atoms.floatingMenuInfo?.get();
  const floatingMenuInfoRowId = floatingMenuState?.rowId;
  const floatingMenuInfoColumnId = floatingMenuState?.columnId;

  const scrollToIndex = scrollToIndexProp ?? (() => undefined);
  const { rowInteractivityClassName, rowInteractivityProps } =
    useRowInteractivityProps(props.row, scrollToIndex);

  const rowLink = props.row.getLinkForRow();
  const rowLinkRef = useRef<HTMLAnchorElement>(null);

  const { setNodeRef } = useSortable({
    id: props.row.id,
    data: {
      row: props.row,
      rowIndex: virtualRowIndex,
      virtualRow,
    },
  });

  const isLongPress = useRef(false);
  // after some researching it looks like the average duration for recognizing long press is ~400-500ms
  // however, this may differ depending on the device
  // Sources:
  // - https://developer.apple.com/documentation/uikit/uilongpressgesturerecognizer/minimumpressduration
  // - https://android.googlesource.com/platform/frameworks/base/+/master/core/java/android/view/ViewConfiguration.java
  const longPressTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleOpenRowLinkOnEnter = (event: KeyboardEvent): void => {
    if (!rowLink) {
      return;
    }

    const isRowEnter =
      (event.target as HTMLElement).getAttribute('role') === 'row' &&
      event.key === 'Enter';

    if (isRowEnter) {
      rowLinkRef.current?.click();
    }
  };

  const handleClickOnRowLink = (event: PointerEvent<HTMLElement>): void => {
    const { target } = event;

    // early exit if a cell actions menu was opened on long press or right-click,
    // or if the target is not a valid element
    if (
      isLongPress.current ||
      (!(target instanceof HTMLElement) && !(target instanceof SVGElement))
    ) {
      return;
    }

    const row = target.closest('[role="row"]');
    // early exit if an element outside or an interactive element inside the row was clicked
    if (row === null || isTargetInteractive(row, target)) {
      return;
    }

    const nativeEvent = event.nativeEvent;
    const newEvent = new MouseEvent('click', {
      ...event.nativeEvent,
      // For some reason the Ctrl and Meta key as well as the button key have to be manually set,
      // even though the native event already contains the correct values
      ctrlKey: nativeEvent.ctrlKey === true,
      metaKey: nativeEvent.metaKey === true,
      button: nativeEvent.button,
    });

    // Dispatching a synthetic event allows to open the link in a new tab in the background
    // when clicking on an cell for which cell actions are defined, for instance.
    rowLinkRef.current?.dispatchEvent(newEvent);
  };

  // get the last left pinned and first right pinned column to be able to display a shadow to indicate overflow
  const leftPinnedColumns = table.getLeftVisibleLeafColumns();
  const lastLeftPinnedColumn = leftPinnedColumns.at(-1);
  const lastLeftPinnedVirtualColumnIndex = leftPinnedColumns.length - 1;
  const rightPinnedColumns = table.getRightVisibleLeafColumns();
  const firstRightPinnedColumn = rightPinnedColumns.at(0);
  const firstRightPinnedVirtualColumnIndex =
    virtualColumns[virtualColumns.length - rightPinnedColumns.length]?.index;

  const gridRowPosition = props.row.getGridRowPosition(virtualItemIndex);
  const visibleCells = props.row.getVisibleCells();

  let linkHandlingEvents = {};
  if (rowLink) {
    linkHandlingEvents = {
      onPointerUp: handleClickOnRowLink,
      onKeyUp: handleOpenRowLinkOnEnter,
    };
  }

  return (
    <>
      <div
        role="row"
        ref={rowMeasure}
        data-rowid={props.row.id}
        data-row={virtualRowIndex}
        className={clsx(
          styles.row({
            rowSeparation: table.atoms.visualVariantConfig?.get().rowSeparation,
            rowParity,
            isDraggedRow,
          }),
          {
            [tableStyles.firstBodyRow]: virtualRowIndex === 0,
            [tableStyles.lastBodyRow]: virtualRowIndex === rowCount - 1,
            [`${rowDetailsExpandedRowCell}`]: props.row.getIsDetailsOpen(),
            [rowInteractivityClassName]: !isInDragOverlay,
          },
        )}
        style={{
          ...gridRowPosition,
        }}
        onTouchStart={() => {
          // we need to set this timeout to detect if the user performed a long press on the row
          longPressTimeout.current = setTimeout(() => {
            isLongPress.current = true;
          }, ROW_LONG_PRESS_TIMEOUT_DURATION);
        }}
        onTouchEnd={() => {
          // clear the long press timer on touch release
          if (longPressTimeout.current !== null) {
            clearTimeout(longPressTimeout.current);
          }
          isLongPress.current = false;
        }}
        // DEPRECATION: [APPDEV-15075] BREAKING-CHANGE: Remove rowInteractivity link
        {...(!isInDragOverlay && {
          ...rowInteractivityProps,
          ...linkHandlingEvents,
        })}
      >
        <DataTableRowContextProvider value={props.row}>
          {virtualColumns.map((virtualCell) => {
            const virtualCellIndex = virtualCell.index;
            const cell = visibleCells[virtualCellIndex];
            const column = cell.column;
            const hasActiveFloatingMenu =
              floatingMenuInfoRowId === props.row.id &&
              floatingMenuInfoColumnId === column.id;

            return (
              <SubRowCellContextProvider
                key={`${virtualRow.key}-${virtualCell.key}`}
                isSubRowIndicatorColumn={
                  hasSubRows && subRowIndicatorColumnId === column.id
                }
                cell={cell}
              >
                <DataTableCell
                  cell={cell}
                  virtualColumnIndex={virtualCellIndex}
                  virtualRowIndex={virtualRowIndex}
                  isInDragOverlay={isInDragOverlay}
                  hasActiveFloatingMenu={hasActiveFloatingMenu}
                  columnMeasure={columnMeasure}
                  dragAndDropRef={setNodeRef}
                  subRowDepth={subRowDepth}
                  thresholdPill={thresholdPill}
                  rowData={props.row.original}
                  cellDeps={column.columnDef?.meta?.cellDeps?.(cell).toString()}
                />
              </SubRowCellContextProvider>
            );
          })}
        </DataTableRowContextProvider>
        {rowLink && (
          // this link element solely holds the link for interactive rows and is not visible to the user
          // it's not directly clickable; instead, the link is opened by interactions with the row (click, enter)
          // eslint-disable-next-line jsx-a11y/anchor-has-content
          <a
            ref={rowLinkRef}
            href={rowLink}
            onPointerDown={(event: PointerEvent) => event.stopPropagation()}
            style={{ display: 'none' }}
          />
        )}
      </div>
      {!isDraggedRow && (
        <DataTablePinningShadowCells
          lastLeftPinnedColumn={{
            column: lastLeftPinnedColumn,
            index: lastLeftPinnedVirtualColumnIndex,
          }}
          firstRightPinnedColumn={{
            column: firstRightPinnedColumn,
            index: firstRightPinnedVirtualColumnIndex,
          }}
          gridRowPosition={gridRowPosition}
          rowId={props.row.id}
        />
      )}
    </>
  );
}
